import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../../lib/utils';
import { borderGrey, mediumText, primaryText, secondaryText, semiBoldText } from '../../../../../styling/stylingHelper';
import { FrontendStaffUser } from '../../../../../services/staff/staffService';
import { useAuth } from '../../../../../services/authentication/authContext';
import { getOperationalPages, hasMainPageRestriction, MAIN_PAGE_RESTRICTION } from '../../../../../services/accessManagement/mainPageRestriction';
import Toggle from '../../../../ui/Toggle';
import { SectionHeading, sectionPanelClass } from './sectionStyles';

interface StaffPermissionsSectionProps {
  user: FrontendStaffUser;
  onPermissionToggle: (permission: string, enabled: boolean) => void;
}

const StaffPermissionsSection: React.FC<StaffPermissionsSectionProps> = ({ user, onPermissionToggle }) => {
  const { t } = useTranslation();
  const { accountSettings, backendUser } = useAuth();

  // Page permissions only show when the account actually has the corresponding page
  // setting; cost price / manage staff are not page-gated and always show.
  const permissionToggles = [
    { key: 'recipe_edit', label: t('staff.recipeEditPermission'), requiresSetting: 'recipes_page' },
    { key: 'inventory_edit', label: t('staff.inventoryEditPermission'), requiresSetting: 'inventory_sections_page' },
    { key: 'mep_edit', label: t('staff.mepEditPermission'), requiresSetting: 'mep_page' },
    { key: 'checklist_edit', label: t('staff.checklistEditPermission'), requiresSetting: 'checklists_page' },
    { key: 'notifications_edit', label: t('staff.notificationsEditPermission'), requiresSetting: 'notifications_page' },
    { key: 'waste_edit', label: t('staff.wasteEditPermission'), requiresSetting: 'waste_page' },
    { key: 'sourcing_improvement', label: t('staff.sourcingImprovementPermission'), requiresSetting: 'sourcing_improvement' },
    { key: 'menu_change_dashboard', label: t('staff.menuChangeDashboardPermission'), requiresSetting: 'menu_change_dashboard' },
    { key: 'cost_price_edit', label: t('staff.costPriceEditPermission') },
    { key: 'manage_staff', label: t('staff.manageStaffPermission') },
  ].filter((p) => !p.requiresSetting || accountSettings.includes(p.requiresSetting));

  const permissions = user.permissions || [];
  const restricted = hasMainPageRestriction({ role: 'staff', permissions });
  const hasNoPages = restricted && backendUser && getOperationalPages({ ...backendUser, permissions, role: 'staff' }, accountSettings).length === 0;
  const blockedPermission = (permission: string) => restricted && ['cost_price_edit', 'sourcing_improvement', 'menu_change_dashboard'].includes(permission);
  const hasAllPermissions = permissions.includes('all_permissions');

  return (
    <section>
      <SectionHeading>{t('staff.permissions')}</SectionHeading>
      <div className={cn(sectionPanelClass, 'mt-3 p-3 sm:p-4')}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className={cn(primaryText, semiBoldText)}>{t('staff.restrictMainPages')}</p>
            <p className={cn(secondaryText, 'mt-1 text-sm')}>{t('staff.restrictMainPagesDescription')}</p>
          </div>
          <Toggle checked={restricted} onChange={enabled => onPermissionToggle(MAIN_PAGE_RESTRICTION, enabled)} ariaLabel={t('staff.restrictMainPages')} />
        </div>
        {hasNoPages && (
          <p role="alert" className="mt-3 rounded-lg border border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-950 p-3 text-sm font-semibold text-amber-900 dark:text-amber-100">
            {t('staff.noPagesWarning')}
          </p>
        )}
      </div>
      <div className={cn(sectionPanelClass, 'mt-3 sm:mt-4 p-3 sm:p-4')}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className={cn(primaryText, semiBoldText, 'text-[14px] sm:text-[16px] leading-5 sm:leading-6')}>
              {t('staff.allPermissions')}
            </p>
            <p className={cn(secondaryText, 'mt-1 text-[14px] leading-5 sm:leading-6 font-normal')}>
              {t('staff.allPermissionsDescription')}
            </p>
          </div>
          <Toggle
            checked={hasAllPermissions}
            onChange={(enabled) => onPermissionToggle('all_permissions', enabled)}
            ariaLabel={t('staff.allPermissions')}
          />
        </div>

        {!hasAllPermissions && (
          <div className="flex flex-col items-center justify-between gap-2.5 sm:gap-4 w-full">
            {permissionToggles.map((perm) => (
              <div
                key={perm.key}
                className={cn(
                  // Single divider under the "all permissions" row, not between every toggle.
                  'w-full flex items-center justify-between gap-3 first:border-t',
                  borderGrey,
                  'first:mt-3 first:pt-3 sm:first:pt-5'
                )}
              >
                <div><span className={cn(primaryText, mediumText, 'text-[14px] leading-5 sm:leading-6')}>{perm.label}</span>
                  {blockedPermission(perm.key) && <p className={cn(secondaryText, 'mt-1 text-sm')}>{t('staff.mainPagePermissionDisabled')}</p>}
                </div>
                <Toggle
                  checked={permissions.includes(perm.key)}
                  disabled={blockedPermission(perm.key)}
                  onChange={(enabled) => onPermissionToggle(perm.key, enabled)}
                  size="sm"
                  ariaLabel={perm.label}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default StaffPermissionsSection;
